// src/components/PremiumStuff/layout/PremiumLandingPage.jsx
import React, { useState, useEffect, useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";

import HeroSection from "../sections/HeroSection.jsx";
import StatsSection from "../sections/StatsSection";
import ServicesSection from "../sections/ServicesSection.jsx";
import ProcessTimeline from "../sections/ProcessTimeline.jsx";
// import TestimonialsSection from "../sections/TestimonialsSection";
import FinalCTA from "../sections/FinalCTA.jsx";
import Footer from "../sections/Footer.jsx";

import Navigation from "./Navigation.jsx";

const PremiumLandingPage = () => {
  const [email, setEmail] = useState("");
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [activeService, setActiveService] = useState(0);
  const [navHeight, setNavHeight] = useState(0);
  const [alertMessage, setAlertMessage] = useState(null);

  const heroRef = useRef(null);
  const servicesRef = useRef(null);
  const statsRef = useRef(null);
  const timelineRef = useRef(null);
  const testimonialRef = useRef(null);
  const ctaRef = useRef(null);

  // Ref for navigation to measure height
  const navRef = useRef(null);

  const { scrollYProgress } = useScroll();
  const opacity = useTransform(scrollYProgress, [0, 0.2], [1, 0.8]);
  const scale = useTransform(scrollYProgress, [0, 0.2], [1, 0.95]);

  useEffect(() => {
    const updateNavHeight = () => {
      if (navRef.current) {
        setNavHeight(navRef.current.offsetHeight);
      }
    };

    // Initial measurement
    updateNavHeight();

    // Update on window resize
    window.addEventListener("resize", updateNavHeight);

    return () => {
      window.removeEventListener("resize", updateNavHeight);
    };
  }, []);

  useEffect(() => {
    let frameId = null;
    const handleMouseMove = (e) => {
      if (frameId) return;
      frameId = window.requestAnimationFrame(() => {
        setMousePosition({ x: e.clientX, y: e.clientY });
        frameId = null;
      });
    };
    window.addEventListener("mousemove", handleMouseMove);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      if (frameId) window.cancelAnimationFrame(frameId);
    };
  }, []);

  const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const handleEmailSubmit = async (e) => {
    e.preventDefault();

    if (!isValidEmail(email)) {
      setAlertMessage("Please enter a valid email address.");
      return;
    }

    setIsLoading(true);

    setTimeout(() => {
      setIsSubmitted(true);
      setIsLoading(false);
      setEmail("");
    }, 1000);
  };

  return (
    <>
      <Navigation ref={navRef} />

      <div
        className="min-h-screen bg-slate-900 text-white"
        style={{ paddingTop: navHeight }}
      >
        <div className="fixed inset-0 overflow-hidden pointer-events-none">
          <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900"></div>
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(16,185,129,0.1),transparent_70%)]"></div>
          <div
            className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(59,130,246,0.05),transparent_70%)]"
            style={{
              transform: `translate(${mousePosition.x * 0.02}px, ${mousePosition.y * 0.02}px)`,
            }}
          ></div>
        </div>
        <HeroSection
          email={email}
          setEmail={setEmail}
          isSubmitted={isSubmitted}
          setIsSubmitted={setIsSubmitted}
          isLoading={isLoading}
          setIsLoading={setIsLoading}
          handleEmailSubmit={handleEmailSubmit}
          opacity={opacity}
          scale={scale}
          ref={heroRef}
          onShowAlert={(message) => setAlertMessage(message)}
        />

        <StatsSection ref={statsRef} />

        <ServicesSection
          activeService={activeService}
          setActiveService={setActiveService}
          ref={servicesRef}
        />

        <ProcessTimeline ref={timelineRef} />

        {/* <TestimonialsSection ref={testimonialRef} /> */}

        <FinalCTA ref={ctaRef} />

        <Footer />

        {/* Alert Modal */}
        {alertMessage && (
          <div 
            className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4" 
            onClick={() => setAlertMessage(null)}
            role="dialog"
            aria-modal="true"
            aria-labelledby="alert-title"
            aria-describedby="alert-message"
          >
            <div 
              className="bg-slate-800 rounded-xl border border-slate-700/50 w-full max-w-md max-h-[90vh] overflow-y-auto shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6">
                <div className="flex justify-between items-center mb-4">
                  <div className="flex items-center">
                    <div className="w-8 h-8 bg-yellow-500/20 rounded-full flex items-center justify-center mr-3">
                      <svg className="w-5 h-5 text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.996-.833-2.664 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                      </svg>
                    </div>
                    <h3 id="alert-title" className="text-lg font-semibold text-white">Alert</h3>
                  </div>
                  <button
                    onClick={() => setAlertMessage(null)}
                    className="text-slate-400 hover:text-white transition-colors p-1 rounded-full hover:bg-slate-700"
                    aria-label="Close alert"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
                <p id="alert-message" className="text-slate-300 mb-6 leading-relaxed">{alertMessage}</p>
                <button
                  onClick={() => setAlertMessage(null)}
                  className="w-full px-4 py-3 bg-gradient-to-r from-emerald-600 to-teal-600 text-white font-semibold rounded-lg hover:from-emerald-700 hover:to-teal-700 transition-all duration-200 transform hover:scale-[1.02] focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 focus:ring-offset-slate-800"
                  autoFocus
                >
                  Got it!
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default PremiumLandingPage;
